<template>
  <div
    class="mb-5 last:mb-0 w-full"
    :class="handleClasses(type)"
    :data-type="type"
  >
    <template v-if="type === 'checkbox'">
      <checkbox
        :id="id"
        :is-required="isRequired"
        @inputWasChanged="handleExternalValidation($event)"
        @input="handleInput($event)"
        :value="value"
      >
        <span v-html="label"></span>
      </checkbox>
    </template>
    <template v-if="type === 'time'">
      <vpf-select
        :placeholder="placeholder"
        :is-required="isRequired"
        :label="label"
        :value="value"
        @input="handleInput($event)"
        @inputWasChanged="handleExternalValidation($event)"
      />
    </template>
    <template v-if="inputType === 'label'">
      <div
        role="heading"
        v-html="label"
        aria-level="2"
        class="text-editor relative text-left md:text-center text-base md:text-xl md:mt-6"
      ></div>
    </template>
    <template v-if="type === 'textarea'">
      <div v-if="label" class="text-base mb-2 flex w-auto items-start">
        <span v-html="label"></span>
        <span v-if="isRequired" class="text-velux-red ml-0.5">*</span>
      </div>
      <textarea
        ref="focusInput"
        :name="name"
        :required="isRequired"
        :placeholder="placeholder"
        :id="id"
        @blur="handleValidation({ isBlur: true })"
        @keyup="handleValidation()"
        @input="handleInput($event)"
        v-model="model[inputType]"
        rows="5"
        class="
          px-4
          w-full
          py-3.5
          md:p-3
          text-base
          md:text-lg
          border
          placeholder-velux-grey-300
          font-bold
        "
        :class="{
          'border-velux-red': hasError()
        }"
      />
      <div
        v-if="hasError() && errorMessage"
        class="text-velux-red mt-1 font-bold text-left text-base"
      >
        {{ errorMessage }}
      </div>
    </template>
    <template v-if="type === 'file'">
      <file-upload
        :is-required="isRequired"
        :label="label"
        :id="id"
        :name="name"
        @inputWasChanged="handleExternalValidation($event)"
      >
        {{ placeholder }}
      </file-upload>
    </template>
    <template
      v-if="
        !isExternalInput && inputType !== 'label' && inputType !== 'textarea'
      "
    >
      <div
        v-if="label"
        class="text-base font-normal mb-2 flex w-auto items-start"
      >
        <span v-html="label"></span>
        <span v-if="isRequired" class="text-velux-red ml-0.5">*</span>
      </div>

      <input
        ref="focusInput"
        :type="inputType"
        :required="isRequired"
        :placeholder="placeholder"
        @blur="handleValidation({ isBlur: true })"
        @keyup="handleValidation()"
        @input="handleInput($event)"
        v-model="model[inputType]"
        :name="name"
        :id="id"
        class="
          px-4
          w-full
          py-3.5
          md:p-3
          text-base
          md:text-lg
          border
          placeholder-velux-grey-300
          font-bold
        "
        :class="{
          'border-velux-red': hasError()
        }"
      />
      <div
        v-if="hasError() && errorMessage"
        class="text-velux-red mt-1 font-bold text-left text-base"
      >
        {{ errorMessage }}
      </div>
    </template>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { email, requiredIf } from "@vuelidate/validators";
import debounce from "../../mixins/debounce";
import Checkbox from "./Checkbox";
import Select from "./Select";
import FileUpload from "./FileUpload.vue";

const isPhoneNumber = value => (value ? /^\+?[0-9]+$/.test(value) : true);
const isNotEmptyString = value => (value ? value.trim() !== "" : true);

export default {
  setup: () => ({ $v: useVuelidate() }),
  mixins: [debounce],
  components: {
    Checkbox,
    FileUpload,
    "vpf-select": Select
  },
  props: {
    type: {
      type: String,
      default: "text"
    },
    id: {
      type: String
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    isValueFromList: {
      type: Boolean,
      default: false
    },
    errorMessage: String,
    label: String,
    placeholder: String,
    name: String,
    value: [String, Boolean, Number],
    wide: Boolean
  },
  data() {
    return {
      isExternalInputValid: false,
      isExternalInput: false,
      debounceTime: 100,
      inputType: undefined,
      inputData: "",
      model: {
        text: this.value,
        email: this.value,
        tel: this.value,
        date: this.value,
        textarea: this.value,
        address: this.value
      },
      classes: {
        textarea: "md:max-w-lg md:mx-auto",
        checkbox: "md:max-w-xl mx-auto text-left",
        label: "md:max-w-xl mx-auto text-left",
        file: "md:max-w-lg md:w-90 md:self-start text-left",
        default: "md:max-w-xxs md:mx-2",
        address: "md:max-w-lg md:mx-auto"
      }
    };
  },
  created() {
    this.isExternalInput =
      this.type === "checkbox" || this.type === "time" || this.type === "file";

    if (!this.isExternalInput) {
      this.inputType = this.type == "input" ? "text" : this.type;
    }
  },
  mounted() {
    this.emitValidation();
  },
  methods: {
    handleClasses(type) {
      let classForType;

      if (this.classes[type]) {
        classForType = this.classes[type];
      } else {
        classForType = this.wide ? this.classes.address : this.classes.default;
      }

      return classForType;
    },
    handleExternalValidation(e) {
      // Validation for components that has their own validation method
      this.isExternalInputValid = e;
      this.$emit("inputWasChanged", this.isExternalInputValid);
    },
    handleValidation(isBlur) {
      this.emitValidation();

      if (isBlur) this.$v.model[this.inputType].$touch();
    },
    handleInput(e) {
      this.debounce(() => {
        this.$emit("input", e);
      }, this.debounceTime);
    },
    emitValidation() {
      this.$emit("inputWasChanged", this.isInvalid);
    },
    hasError() {
      return this.isInvalid && this.$v.model[this.inputType].$dirty;
    }
  },
  computed: {
    isInvalid() {
      return !this.isExternalInput
        ? this.$v.model[this.inputType].$invalid
        : this.isExternalInputValid;
    }
  },
  validations: {
    model: {
      tel: {
        required: requiredIf(function() {
          return this.isRequired;
        }),
        isPhoneNumber
      },
      date: {
        required: requiredIf(function() {
          return this.isRequired;
        })
      },
      text: {
        required: requiredIf(function() {
          return this.isRequired;
        }),
        isNotEmptyString
      },
      address: {
        required: requiredIf(function() {
          return this.isRequired;
        }),
        isNotEmptyString,
        function() {
          return this.isValueFromList;
        }
      },
      textarea: {
        required: requiredIf(function() {
          return this.isRequired;
        }),
        isNotEmptyString
      },
      email: {
        required: requiredIf(function() {
          return this.isRequired;
        }),
        isNotEmptyString,
        email
      },
      label: {}
    }
  }
};
</script>
